/**
 * Sidebar-specific configuration - contains only configurations specific to the sidebar component
 */

import { Component, Landmark, FileQuestion, LayoutTemplate, BellDot, Grid2x2Plus, Braces, FileInput, FileX2, FileStack, TextSearch, ChartLine, FileChartPie, Cog, MonitorCog, UserCog, BookText, FileCode2, ChartPie, Users, Webhook, Trash2, LayoutDashboard, BarChart4, TrendingUp, Lightbulb, Database, Link2, ShieldCheck, AlertTriangle, Download } from 'lucide-react'

// Sidebar menu items configuration (specific to sidebar)
export const SIDEBAR_ITEMS = {
  aa: [
    { id: 'consent-requests', label: 'Consent Requests', icon: FileStack },
    { id: 'consent-templates', label: 'Consent Templates', icon: LayoutTemplate },
    { id: 'fi-notifications', label: 'FI Notifications', icon: BellDot },
    { id: 'data-fetch-request', label: 'Data Fetch Request', icon: Grid2x2Plus },
    { id: 'fi-data-purge', label: 'FI Data Purge', icon: Trash2 },
  ],
  bulk: [
    { id: 'bulk-consent-request', label: 'Bulk Consent Request', icon: Braces },
    { id: 'bulk-data-fetch', label: 'Bulk Data Fetch', icon: FileInput },
    { id: 'bulk-consent-revoke', label: 'Bulk Consent Revoke', icon: FileX2 },
    { id: 'bulk-csv-upload', label: 'Bulk CSV Upload', icon: FileStack },
    { id: 'bulk-mis', label: 'Bulk MIS', icon: ChartPie },
  ],
  analytics: [
    { id: 'insights', label: 'Insights', icon: TextSearch },
    { id: 'analytics', label: 'Analytics', icon: ChartLine },
    { id: 'pdf-analytics', label: 'PDF Analytics', icon: FileChartPie },
    // EWS removed - now fully config-driven from backend
  ],
  pfm: [
    { id: 'user-analytics', label: 'User Analytics', icon: BarChart4 },
  ],
  admin: [
    { id: 'settings', label: 'Settings', icon: Cog },
    { id: 'manage-apps', label: 'Manage Apps', icon: MonitorCog },
    { id: 'admin', label: 'Admin', icon: UserCog },
  ],
  docs: [
    { id: 'account-aggregator', label: 'Account Aggregator', icon: Component },
    { id: 'fip', label: 'FIP', icon: Landmark },
    { id: 'purpose', label: 'Purpose', icon: FileQuestion },
    { id: 'central-registry', label: 'Central Registry', icon: BookText },
    { id: 'integration-docs', label: 'Integration Docs', icon: FileCode2 },
    { id: 'mis', label: 'MIS', icon: ChartPie },
  ],
}

// Sidebar group configuration (specific to sidebar)
export const SIDEBAR_GROUPS = [
  { id: 'aa', label: 'AA ECOSYSTEM' },
  { id: 'bulk', label: 'BULK OPERATIONS' },
  { id: 'analytics', label: 'ANALYTICS' },
  { id: 'pfm', label: 'PFM' },
  { id: 'admin', label: 'ADMIN & SETUP' },
  { id: 'docs', label: 'REFERENCE & DOCS' },
]

/**
 * Maps backend menu codes to custom display labels
 * Used to override default menu item names from backend
 */
export const CODE_TO_LABEL_OVERRIDE_MAP: Record<string, string> = {
  'SBI-Metrics': 'AA Metrics',
  'MIS-INSIGHTS': 'AA-Insights',  // Rename MIS-INSIGHTS to AA-Insights
}

// Component defaults (specific to sidebar)
export const SIDEBAR_DEFAULTS = {
  selectedItem: 'consent-templates',
  headerText: '/*workasaur 🦖',
  expanded: false,
}

/**
 * Group configuration for transforming backend menuList into sidebar groups
 * Defines which backend permission codes belong to which visual groups
 */
export const GROUP_CONFIG = [
  {
    id: 'aa',
    label: 'AA ECOSYSTEM',
    order: 1,
    defaultOpen: true,
    codes: [
      'CONSENT-TEMPLATE',
      'CONSENT-REQUEST',
      'FI-Notifications',
      'DATA-FETCH-REQUEST',
      'FI-DATA-PURGE',
      'DOWNLOAD-CENTRE',
    ],
  },
  {
    id: 'bulk',
    label: 'BULK OPERATIONS',
    order: 2,
    defaultOpen: true,
    codes: [
      'BULK-CONSENT-REQUEST',
      'BULK-DATA-FETCH',
      'BULK-CONSENT-REVOKE',
      'BULK-CSV-UPLOAD',
      'BULK-MIS',
    ],
  },
  {
    id: 'insights',
    label: 'INSIGHTS',
    order: 3,
    defaultOpen: true,
    codes: [
      'MIS-INSIGHTS',  // Will be renamed to AA-Insights via label override
      'EWS',  // Backend sends 'EWS' code (config-driven, only shows if present in menuList)
    ],
  },
  {
    id: 'analytics',
    label: 'ANALYTICS',
    order: 4,
    defaultOpen: true,
    codes: ['INSIGHTS', 'ANALYTICS', 'PDF-ANALYTICS'],
  },
  {
    id: 'pfm',
    label: 'PFM',
    order: 4.5,
    defaultOpen: true,
    codes: ['USER-ANALYTICS'],
  },
  {
    id: 'admin',
    label: 'ADMIN & SETUP',
    order: 5,
    defaultOpen: true,
    codes: ['SETTINGS', 'MANAGE-APPS', 'ADMIN'],
  },
  {
    id: 'docs',
    label: 'REFERENCE & DOCS',
    order: 6,
    defaultOpen: true,
    codes: ['ACCOUNT-AGGREGATOR', 'FIP', 'PURPOSE', 'CENTRAL-REGISTRY', 'INTEGRATIONDOCS', 'MIS'],
  },
]

/**
 * Maps backend menu codes to Lucide icon components
 * Used during transformation to assign correct icons to menu items
 */
export const CODE_TO_ICON_MAP: Record<string, any> = {
  'ACCOUNT-AGGREGATOR': Component,
  'FIP': Landmark,
  'PURPOSE': FileQuestion,
  'CONSENT-TEMPLATE': LayoutTemplate,
  'CONSENT-REQUEST': FileStack,
  'FI-Notifications': BellDot,
  'DATA-FETCH-REQUEST': Grid2x2Plus,
  'FI-DATA-PURGE': Trash2,
  'DOWNLOAD-CENTRE': Download,
  'BULK-CONSENT-REQUEST': Braces,
  'BULK-DATA-FETCH': FileInput,
  'BULK-CONSENT-REVOKE': FileX2,
  'BULK-CSV-UPLOAD': FileStack,
  'BULK-MIS': ChartPie,
  'INSIGHTS': TextSearch,
  'ANALYTICS': ChartLine,
  'PDF-ANALYTICS': FileChartPie,
  'EARLY-WARNING-SIGNALS': AlertTriangle, // Legacy code, kept for backward compatibility
  'EWS': AlertTriangle, // Backend now sends 'EWS' code
  'SETTINGS': Cog,
  'MANAGE-APPS': MonitorCog,
  'ADMIN': UserCog,
  'USERS': UserCog,
  'USERSLIST': Users,
  'WEBHOOKS': Webhook,
  'CENTRAL-REGISTRY': BookText,
  'INTEGRATIONDOCS': FileCode2,
  'MIS': ChartPie,
  // MIS submenu items
  'MIS-DASHBOARD': LayoutDashboard,
  'MIS-Metrics': BarChart4,
  'MIS-FIP-Funnel': TrendingUp,
  'SBI-Metrics': BarChart4,
  'MIS-INSIGHTS': Lightbulb,
  'USER-ANALYTICS': BarChart4,
  // Central Registry submenu items
  'FETCH-RECORD': Database,
  'CR-DASHBOARD': LayoutDashboard,
  'AA-FIP-MAPING': Link2,
  'FIP-SAANS': ShieldCheck,
}

/**
 * Maps backend menu codes to sidebar item IDs (lowercase, hyphenated)
 * Ensures consistent naming between backend and frontend
 */
export const CODE_TO_ITEM_ID_MAP: Record<string, string> = {
  'ACCOUNT-AGGREGATOR': 'account-aggregator',
  'FIP': 'fip',
  'PURPOSE': 'purpose',
  'CONSENT-TEMPLATE': 'consent-templates',
  'CONSENT-REQUEST': 'consent-requests',
  'FI-Notifications': 'fi-notifications',
  'DATA-FETCH-REQUEST': 'data-fetch-request',
  'FI-DATA-PURGE': 'fi-data-purge',
  'DOWNLOAD-CENTRE': 'download-centre',
  'BULK-CONSENT-REQUEST': 'bulk-consent-request',
  'BULK-DATA-FETCH': 'bulk-data-fetch',
  'BULK-CONSENT-REVOKE': 'bulk-consent-revoke',
  'BULK-CSV-UPLOAD': 'bulk-csv-upload',
  'BULK-MIS': 'bulk-mis',
  'INSIGHTS': 'insights',
  'ANALYTICS': 'analytics',
  'PDF-ANALYTICS': 'pdf-analytics',
  'EARLY-WARNING-SIGNALS': 'early-warning-signals', // Legacy code
  'EWS': 'early-warning-signals', // Backend now sends 'EWS' code
  'SETTINGS': 'settings',
  'MANAGE-APPS': 'manage-apps',
  'ADMIN': 'admin',
  'USERS': 'users',
  'USERSLIST': 'users-list',
  'WEBHOOKS': 'webhooks',
  'CENTRAL-REGISTRY': 'central-registry',
  'INTEGRATIONDOCS': 'integration-docs',
  'MIS': 'mis',
  // MIS submenu items
  'MIS-DASHBOARD': 'mis-dashboard',
  'MIS-Metrics': 'mis-metrics',
  'MIS-FIP-Funnel': 'mis-fip-funnel',
  'SBI-Metrics': 'sbi-metrics',
  'MIS-INSIGHTS': 'mis-insights',
  'USER-ANALYTICS': 'user-analytics',
  // Central Registry submenu items
  'FETCH-RECORD': 'fetch-record',
  'CR-DASHBOARD': 'cr-dashboard',
  'AA-FIP-MAPING': 'aa-fip-mapping',
  'FIP-SAANS': 'fip-saans',
}

/**
 * Maps backend menu codes to Angular routes
 * This must match the ACTUAL routes in dashboard-routing.module.ts
 */
export const CODE_TO_ROUTE_MAP: Record<string, string> = {
  'ACCOUNT-AGGREGATOR': '/dashboard/aa-list/all',
  'FIP': '/dashboard/fip-list',
  'PURPOSE': '/dashboard/purpose',
  'CONSENT-TEMPLATE': '/dashboard/products',
  'CONSENT-REQUEST': '/dashboard/consentRequest',
  'FI-Notifications': '/dashboard/finotifications',
  'DATA-FETCH-REQUEST': '/dashboard/dataFetchRequest',
  'FI-DATA-PURGE': '/dashboard/fi-data-purge',
  'DOWNLOAD-CENTRE': '/dashboard/download-centre',
  'BULK-CONSENT-REQUEST': '/dashboard/bulkConsentRequest',
  'BULK-DATA-FETCH': '/dashboard/bulkDataFetch',
  'BULK-CONSENT-REVOKE': '/dashboard/bulkConsentRevoke',
  'BULK-CSV-UPLOAD': '/dashboard/bulkCsvUpload',
  'BULK-MIS': '/dashboard/bulk-mis',
  'INSIGHTS': '/dashboard/insights',
  'ANALYTICS': '/dashboard/analytics',
  'PDF-ANALYTICS': '/dashboard/pdf-analytics',
  'EARLY-WARNING-SIGNALS': '/dashboard/insights/ews', // Legacy code
  'EWS': '/dashboard/insights/ews', // Backend now sends 'EWS' code
  'SETTINGS': '/dashboard/settings',
  'MANAGE-APPS': '/dashboard/manage',
  'ADMIN': '/dashboard/admin',
  'USERS': '/dashboard/admin/users-list',
  'USERSLIST': '/dashboard/admin/users-list',
  'WEBHOOKS': '/dashboard/webhook',
  'CENTRAL-REGISTRY': '/dashboard/sync-entity',
  'INTEGRATIONDOCS': '/dashboard/integration-docs',
  'MIS': '/dashboard/mis',
  // MIS submenu items
  'MIS-DASHBOARD': '/dashboard/mis/dashboard',
  'MIS-Metrics': '/dashboard/mis/metrics',
  'MIS-FIP-Funnel': '/dashboard/mis/fip-board',
  'SBI-Metrics': '/dashboard/mis/sbiMetric',
  'MIS-INSIGHTS': '/dashboard/funnel',
  'USER-ANALYTICS': '/dashboard/pfm/UserAnalytics',
  // Central Registry submenu items
  'FETCH-RECORD': '/dashboard/sync-entity',
  'CR-DASHBOARD': '/dashboard/cr-activity-dashboard',
  'AA-FIP-MAPING': '/dashboard/aa-fip-mapping',
  'FIP-SAANS': '/dashboard/fip-saans',
}
